import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { verifyPasswordSetupToken, completePasswordSetup } from "../lib/api";
import Logo from "../components/Logo";

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [phase, setPhase] = useState("verifying"); // verifying | ready | saving | done | error
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setPhase("error");
      setError("Missing token. Please use the link from your email.");
      return;
    }
    (async () => {
      try {
        const res = await verifyPasswordSetupToken(token);
        setEmail(res.email || "");
        setPhase("ready");
      } catch (e) {
        setPhase("error");
        setError(e?.response?.data?.detail || "Invalid or expired link.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setPhase("saving");
    try {
      await completePasswordSetup(token, password);
      setPhase("done");
    } catch (err) {
      setPhase("ready");
      setError(err?.response?.data?.detail || "Could not save the password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF6] flex items-center justify-center p-4 lm-font">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo size={44} />
        </div>
        <div className="bg-white rounded-2xl border border-[#11151C]/10 shadow-sm p-7" data-testid="set-password-card">
          {phase === "verifying" && (
            <div className="text-center py-8">
              <Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B3D]" />
              <p className="mt-3 text-sm text-[#11151C]/60">Verifying your link…</p>
            </div>
          )}

          {phase === "error" && (
            <div className="text-center py-2">
              <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-red-500" />
              </div>
              <h1 className="lm-font font-extrabold text-[#11151C] text-xl mt-4">Link unavailable</h1>
              <p className="text-sm text-[#11151C]/60 mt-2" data-testid="set-password-error">{error}</p>
              <Link to="/login" className="mt-5 inline-block text-[#FF6B3D] font-bold text-sm hover:underline">
                Back to sign in →
              </Link>
            </div>
          )}

          {phase === "done" && (
            <div className="text-center py-2">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <h1 className="lm-font font-extrabold text-[#11151C] text-xl mt-4">Password updated</h1>
              <p className="text-sm text-[#11151C]/60 mt-2">
                You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate("/login")}
                data-testid="set-password-go-login"
                className="lm-btn-primary mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
              >
                Continue to sign in
              </button>
            </div>
          )}

          {(phase === "ready" || phase === "saving") && (
            <form onSubmit={submit}>
              <h1 className="lm-font font-extrabold text-[#11151C] text-2xl">Set your password</h1>
              <p className="text-sm text-[#11151C]/60 mt-1.5">
                Choose a new password for <span className="font-semibold text-[#11151C]">{email || "your account"}</span>.
              </p>

              <label className="block mt-5">
                <span className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65">New password</span>
                <div className="mt-1.5 relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                    autoFocus
                    data-testid="set-password-new"
                    className="w-full border border-[#11151C]/15 rounded-xl px-3.5 py-2.5 text-sm pr-10 focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-[#11151C]/50 hover:text-[#11151C]"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <label className="block mt-3.5">
                <span className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65">Confirm password</span>
                <input
                  type={showPw ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                  data-testid="set-password-confirm"
                  className="mt-1.5 w-full border border-[#11151C]/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                />
              </label>

              {error && (
                <p className="mt-3 text-xs text-red-500" data-testid="set-password-form-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={phase === "saving"}
                data-testid="set-password-submit"
                className="lm-btn-primary mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold disabled:opacity-50"
              >
                {phase === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Save password
              </button>

              <p className="text-[11px] text-[#11151C]/45 mt-3 text-center">
                The link is valid for a short window for security. After saving, sign in with your email and the new password.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
